import SettingsIcon from '@mui/icons-material/Settings';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import L from 'leaflet';
import { SecurityInventoryCategory, SecurityInventoryItem } from '@x-guard/xgac-types/xgac';
import {
  Identifier, useTranslate, Confirm, useRedirect,
} from 'react-admin';
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  useGetList, useRefresh, useNotify, useGetIdentity,
} from 'ra-core';
import { useSearchParams } from 'react-router-dom';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button, IconButton,
} from '@mui/material';
import { useDebounce } from 'use-debounce';
import {
  hasItemPosition, itemToLatLng, securityItemsResource, appUsersResource,
  InventoryItemFilter,
  InventoryItemStatus,
} from '../util';
import { BulkActionsSection } from './BulkActionsSection';
import { CreateItemSection } from './CreateItemSection';
import { FilterButton } from './FilterButton';
import { SearchInput } from '../../../components/SearchInput';
import { SelectAllButton } from './SelectAllButton';
import { InventoryItemsDatagrid } from './InventoryItemsDatagrid';
import { xgacDataProvider } from '../../../../../dataProviders/xgacDataProvider';
import { EditItemModal } from './EditItemModal';
import { RenewalChoiceDialog } from '../../../components/RenewalChoiceDialog';
import { RenewNowFormValues, RenewNowModal } from '../../../components/RenewNowModal';
import { PlanRenewalModal } from '../../../components/PlanRenewalModal';
import { notifyWithError } from '../../../../../utils/errorHandling';
import { getExpiryStatus } from '../../../../../utils/certificationsAndItemsExpiry';
import { calculateLargestReminderPeriodInDays } from '../../../../../utils/timeGranularityUtils';

export const InventoryItemsSidebar = (props: {
  itemsData: SecurityInventoryItem[];
  itemsLoading: boolean;
  categoriesData: SecurityInventoryCategory[];
  categoriesLoading: boolean;
  map: L.Map | null;
  focusedItemId: Identifier | null;
  onItemFocus: (itemId: Identifier | null) => void;
  hoveredItemId: Identifier | null;
  onItemHover: (itemId: Identifier | null) => void;
  onItemsFiltered: (items: SecurityInventoryItem[]) => void;
}) => {

  const redirect = useRedirect();
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();

  const [selectedIds, setSelectedIds] = useState<Identifier[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);
  const [editItemModalOpen, setEditItemModalOpen] = useState(false);
  const [editItemRecord, setEditItemRecord] = useState<SecurityInventoryItem | null>(null);
  const idsBeforeCreate = useRef<Set<Identifier>>(new Set());
  const [openEditAfterCreate, setOpenEditAfterCreate] = useState(false);
  const [createItemFlowActive, setCreateItemFlowActive] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [renewDialogOpen, setRenewDialogOpen] = useState(false);
  const [renewDialogChoice, setRenewDialogChoice] = useState<'renew_now' | 'plan_renewal' | 'closed'>('closed');
  const [renewFlowSessionKey, setRenewFlowSessionKey] = useState(0);
  const [currentUserItemsExpanded, setCurrentUserItemsExpanded] = useState(true);
  const [otherItemsExpanded, setOtherItemsExpanded] = useState(true);
  const [currentUserItemsVisible, setCurrentUserItemsVisible] = useState(true);
  const [otherItemsVisible, setOtherItemsVisible] = useState(true);

  const showBulkActions = selectedIds.length > 0;
  const map = props.map;
  const onItemFocus = props.onItemFocus;
  const onItemsFiltered = props.onItemsFiltered;

  const editItemId = searchParams.get('editItem');
  const categoryId = searchParams.get('category');

  const statusParam = searchParams.get('status');
  const status: InventoryItemStatus | null = statusParam === 'valid' || statusParam === 'expiring' || statusParam === 'expired'
    ? statusParam
    : null;

  const [selectedFilters, setSelectedFilters] = useState<InventoryItemFilter>({ categories: categoryId ? [categoryId] : [], status: status ? [status] : [] });

  const createItemCategoryId = searchParams.get('createItem');

  const { data: identity, isLoading: identityLoading } = useGetIdentity();

  // For all of the fetched resources, all of the data is fetched at once without pagination
  const { data: usersData, isLoading: usersLoading } = useGetList(appUsersResource, {
    pagination: { page: 1, perPage: -1 },
    sort: { field: 'name', order: 'ASC' },
    filter: {},
  });

  const currentAssetId = useMemo(
    () => {

      if (identityLoading || usersLoading || !identity?.id) return null;

      return usersData?.find((user) => user.user?._id && user.user._id === identity.id)?._id;

    },
    [usersData, identity?.id, identityLoading, usersLoading],
  );

  const peopleToNotifyByCategoryId = useMemo(() => {

    const peopleToNotify = new Map<string, Set<string>>();
    for (const category of props.categoriesData) {

      peopleToNotify.set(
        category._id,
        new Set(
          (category.notifiedPeople?.peopleToNotify ?? [])
            .map((entry) => (typeof entry === 'string' ? entry : entry._id)),
        ),
      );

    }
    return peopleToNotify;

  }, [props.categoriesData]);

  // Both search and filter are applied together
  const filteredItems = useMemo(() => {

    if (props.itemsLoading || props.categoriesLoading) return [];

    const list = props.itemsData ?? [];
    const q = debouncedSearchTerm.trim().toLowerCase();

    const { categories: selectedCategories, status: selectedStatuses } = selectedFilters;

    const reminderDaysByCategoryId = Object.fromEntries(
      props.categoriesData.map((category) => [
        category._id,
        calculateLargestReminderPeriodInDays(category.reminderPeriods ?? []),
      ]),
    );

    return list.filter((item) => {

      if (q && !(item.name.toLowerCase().includes(q))) return false;

      const expiryStatus = getExpiryStatus(item.expiryDate, reminderDaysByCategoryId[item.category._id]);
      if (selectedStatuses.length > 0 && !selectedStatuses.includes(expiryStatus)) return false;

      if (selectedCategories.length > 0 && !selectedCategories.includes(item.category._id)) return false;

      return true;

    });

  }, [props.itemsLoading, props.categoriesLoading, props.itemsData, props.categoriesData, debouncedSearchTerm, selectedFilters]);

  const [currentUserItems, remainingItems] = useMemo<[SecurityInventoryItem[], SecurityInventoryItem[]]>(() => {

    const userItems: SecurityInventoryItem[] = [];
    const otherItems: SecurityInventoryItem[] = [];

    for (const item of filteredItems) {

      if (currentAssetId && peopleToNotifyByCategoryId.get(item.category._id)?.has(currentAssetId)) {

        userItems.push(item);

      } else {

        otherItems.push(item);

      }

    }

    return [userItems, otherItems];

  }, [currentAssetId, filteredItems, peopleToNotifyByCategoryId]);

  // Items that are available for selection (expanded and NOT hidden with the eye icon)
  const { selectableItems, selectableItemIds } = useMemo(() => {

    let itemsAvailableForSelection: SecurityInventoryItem[] = [];

    const currentUserItemsSelectable = currentUserItemsExpanded && currentUserItemsVisible;
    const otherItemsSelectable = otherItemsExpanded && otherItemsVisible;

    // if both item groups are expanded and visible, make all filtered items available for selection
    if (currentUserItemsSelectable && otherItemsSelectable) itemsAvailableForSelection = filteredItems;
    // if only the current user items are expanded and visible, make only those items available for selection
    if (currentUserItemsSelectable && !otherItemsSelectable) itemsAvailableForSelection = currentUserItems;
    // if only the other items are expanded and visible, make only those items available for selection
    if (!currentUserItemsSelectable && otherItemsSelectable) itemsAvailableForSelection = remainingItems;

    return {
      selectableItems: itemsAvailableForSelection,
      selectableItemIds: new Set<Identifier>(itemsAvailableForSelection.map((item) => item._id)),
    };

  }, [
    currentUserItemsExpanded, otherItemsExpanded, currentUserItemsVisible, otherItemsVisible,
    filteredItems, currentUserItems, remainingItems,
  ]);

  // Items that are visible on the map (expanded/collapsed and NOT hidden with the eye icon)
  const { visibleItems, visibleItemIds } = useMemo(() => {

    let mapItems: SecurityInventoryItem[] = [];

    // if both item groups are visible, show all filtered items on the map
    if (currentUserItemsVisible && otherItemsVisible) mapItems = filteredItems;
    // if only the current user items are visible, show only those items on the map
    if (currentUserItemsVisible && !otherItemsVisible) mapItems = currentUserItems;
    // if only the other items are visible, show only those items on the map
    if (!currentUserItemsVisible && otherItemsVisible) mapItems = remainingItems;

    return {
      visibleItems: mapItems,
      visibleItemIds: new Set<Identifier>(mapItems.map((item) => item._id)),
    };

  }, [currentUserItemsVisible, otherItemsVisible, filteredItems, currentUserItems, remainingItems]);

  const selectedItems = useMemo(
    () => props.itemsData.filter((item) => selectedIds.includes(item._id)),
    [props.itemsData, selectedIds],
  );

  // Users can select items from multiple categories, but we need to use something to pre-calculate expiry date with.
  // For this purpose, we will use the category of the majority (but users still can edit the expiry date manually)
  const primaryCategory = useMemo(() => {

    if (selectedItems.length === 0) return null;
    const counts = new Map<string, number>();
    for (const item of selectedItems) {

      const currentCategoryId = item.category?._id;
      if (currentCategoryId) {

        counts.set(currentCategoryId, (counts.get(currentCategoryId) ?? 0) + 1);

      }

    }
    if (counts.size === 0) return null;
    let topCategoryId: string | null = null;
    let topCount = 0;
    for (const [currentCategoryId, count] of counts) {

      if (count > topCount) {

        topCount = count;
        topCategoryId = currentCategoryId;

      }

    }
    return props.categoriesData.find((c) => c._id === topCategoryId) ?? null;

  }, [selectedItems, props.categoriesData]);

  const focusItemFromList = useCallback((itemId: Identifier, item: SecurityInventoryItem) => {

    onItemFocus(itemId);
    if (!hasItemPosition(item) || !map) return;

    const latLng = L.latLng(itemToLatLng(item));
    if (!map.getBounds().contains(latLng)) map.flyTo(latLng);

  }, [map, onItemFocus]);

  const closeRenewDialog = () => {

    setRenewDialogChoice('closed');
    setRenewDialogOpen(false);
    setRenewFlowSessionKey((k) => k + 1);

  };

  const updateSelectedItems = async (data: Partial<SecurityInventoryItem>) => {

    if (selectedItems.length === 0) return;
    await Promise.all(
      selectedItems.map((item) => xgacDataProvider.update(securityItemsResource, {
        id: item._id,
        data,
        previousData: item,
      })),
    );

  };

  const openEditItemModal = useCallback((item: SecurityInventoryItem) => {

    setEditItemRecord(item);
    setEditItemModalOpen(true);
    focusItemFromList(item._id, item);

  }, [focusItemFromList]);

  const closeEditItemModal = () => {

    setEditItemModalOpen(false);
    setEditItemRecord(null);
    setCreateItemFlowActive(false);

  };

  const clearCreateItemParam = useCallback(() => {

    setSearchParams((params) => {

      const updated = new URLSearchParams(params);
      updated.delete('createItem');
      return updated;

    }, { replace: true });

  }, [setSearchParams]);

  const handleBeforeCreate = () => {

    idsBeforeCreate.current = new Set(props.itemsData.map((item) => item._id));
    setCreateItemFlowActive(true);

  };

  const handleAfterCreate = () => {

    setOpenEditAfterCreate(true);

  };

  const handleDeleteItems = async () => {

    if (selectedIds.length === 0) return;
    try {

      await xgacDataProvider.deleteMany(securityItemsResource, { ids: selectedIds });
      notify('ra.notification.deleted', { type: 'info', messageArgs: { smart_count: selectedIds.length } });
      refresh();
      if (props.focusedItemId && selectedIds.includes(props.focusedItemId)) {

        onItemFocus(null);

      }
      setSelectedIds([]);
      setDeleteDialogOpen(false);

    } catch (error) {

      notifyWithError(notify, error);
      setDeleteDialogOpen(false);

    }

  };

  useEffect(() => {

    if (!openEditAfterCreate) return;

    const newItems = props.itemsData.filter((item) => !idsBeforeCreate.current.has(item._id));
    if (newItems.length === 0) return;

    openEditItemModal(newItems[0]);
    setOpenEditAfterCreate(false);

  }, [openEditAfterCreate, props.itemsData, openEditItemModal, setOpenEditAfterCreate]);

  useEffect(() => {

    if (!editItemId || props.itemsLoading) return;

    const item = props.itemsData.find((record) => record._id === editItemId);
    if (!item) {

      setEditItemRecord(null);
      setEditItemModalOpen(false);
      return;

    }

    openEditItemModal(item);
    setSearchParams((params) => {

      const updatedSearchParams = new URLSearchParams(params);
      updatedSearchParams.delete('editItem');
      return updatedSearchParams;

    }, { replace: true });

  }, [editItemId, props.itemsData, props.itemsLoading, openEditItemModal, setSearchParams]);

  useEffect(() => {

    if (!categoryId && !status) return;

    setSelectedFilters((prev) => ({
      ...prev,
      categories: categoryId ? [categoryId] : [],
      status: status ? [status] : [],
    }));

    setSearchParams((current) => {

      const updated = new URLSearchParams(current);
      updated.delete('category');
      updated.delete('status');
      return updated;

    }, { replace: true });

  }, [categoryId, status, setSelectedFilters, setSearchParams]);

  useEffect(() => {

    onItemsFiltered(visibleItems);
    setSelectedIds((currentIds) => {

      const displayedSelectedIds = currentIds.filter((id) => selectableItemIds.has(id));
      return displayedSelectedIds.length === currentIds.length ? currentIds : displayedSelectedIds;

    });

  }, [onItemsFiltered, visibleItems, selectableItemIds]);

  useEffect(() => {

    if (props.focusedItemId !== null && !visibleItemIds.has(props.focusedItemId)) {

      onItemFocus(null);

    }

  }, [props.focusedItemId, onItemFocus, visibleItemIds]);

  useEffect(() => {

    if (!props.focusedItemId) return;
    requestAnimationFrame(() => {

      document.getElementById(`inventory-item-${props.focusedItemId}`)?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });

    });

  }, [props.focusedItemId]);

  const handleRenewNowSubmit = async (values: RenewNowFormValues) => {

    if (!('lastInspectionDate' in values)) return;

    const data = {
      lastInspectionDate: new Date(values.lastInspectionDate).toISOString(),
      expiryDate: new Date(values.expiryDate).toISOString(),
    };
    try {

      await updateSelectedItems(data);
      notify('ra.notification.updated', {
        type: 'success',
        messageArgs: { smart_count: selectedItems.length },
      });
      refresh();
      setSelectedIds([]);
      closeRenewDialog();

    } catch (error) {

      notifyWithError(notify, error);
      closeRenewDialog();

    }

  };

  const handlePlanRenewalSubmit = async (values: {
    plannedRenewalDate: string;
  }) => {

    const data = {
      plannedRenewalDate: new Date(values.plannedRenewalDate).toISOString(),
    };
    try {

      await updateSelectedItems(data);
      notify('ra.notification.updated', {
        type: 'success',
        messageArgs: { smart_count: selectedItems.length },
      });
      refresh();
      setSelectedIds([]);
      closeRenewDialog();

    } catch (error) {

      notifyWithError(notify, error);
      closeRenewDialog();

    }

  };

  return (
    <>
      <div className="button-sidebar-div">
        <div className="button-sidebar-add">
          <Box sx={{
            alignItems: 'center', display: 'flex', gap: 1, justifyContent: 'space-between', marginBottom: '1em', marginTop: '0.5em',
          }}>
            <h4 style={{ margin: 0 }}>
              <span>{showBulkActions ? translate('resources.buttons.text.actions_with_selected') : translate('menu.items.security_inventory')}</span>
            </h4>
            {!showBulkActions && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={() => redirect('list', 'security-inventory-categories')}
                sx={{ marginTop: '2px' }}
              >
                {translate('general.text.settings')}
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {showBulkActions && <BulkActionsSection
              onDelete={() => setDeleteDialogOpen(true)}
              onRenew={() => {

                setRenewDialogOpen(true);
                setRenewFlowSessionKey((k) => k + 1);

              }}
            />}
            {!showBulkActions && <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <CreateItemSection
                categories={props.categoriesData || []}
                categoriesLoading={props.categoriesLoading}
                createItemFlowActive={createItemFlowActive}
                map={map}
                onBeforeCreate={handleBeforeCreate}
                onAfterCreate={handleAfterCreate}
                initialCreateCategoryId={createItemCategoryId}
                onInitialCreateCategory={clearCreateItemParam}
              />
              <FilterButton
                categories={props.categoriesData || []}
                filters={selectedFilters}
                onChange={setSelectedFilters}
              >
                <SearchInput
                  searchTerm={searchTerm}
                  onChange={setSearchTerm}
                  placeholder={translate('resources.security-inventory-items.text.search')}
                  variant="sidebar"
                />
              </FilterButton>
            </Box>}
            {showBulkActions && (
              <SelectAllButton
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
                selectableItems={selectableItems}
              />
            )}
          </Box>
        </div>
        <div className="button-sidebar-grid">
          <Accordion
            expanded={currentUserItemsExpanded}
            onChange={(_, expanded) => setCurrentUserItemsExpanded(expanded)}
            disableGutters
            elevation={0}
            slotProps={{ transition: { unmountOnExit: true } }}
            sx={{
              '&::before': {
                display: 'none',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                flexDirection: 'row-reverse',
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center',
                  justifyContent: 'space-between',
                },
                '& .MuiAccordionSummary-expandIconWrapper': {
                  marginRight: 1,
                },
              }}
            >
              <Box component="span" sx={{ fontWeight: 600 }}>
                {translate('resources.security-inventory-items.text.items_i_am_responsible_for', {
                  count: currentUserItems.length,
                })}
              </Box>
              <IconButton
                size="small"
                onClick={(event) => {

                  event.stopPropagation();
                  setCurrentUserItemsVisible((visible) => !visible);

                }}
              >
                {currentUserItemsVisible ? <VisibilityIcon color="action" /> : <VisibilityOffIcon color="disabled" />}
              </IconButton>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              <InventoryItemsDatagrid
                items={currentUserItems}
                isLoading={props.itemsLoading || props.categoriesLoading || usersLoading || identityLoading}
                categories={props.categoriesData || []}
                visible={currentUserItemsVisible}
                selectable={currentUserItemsExpanded && currentUserItemsVisible}
                hoveredItemId={props.hoveredItemId}
                selectedIds={selectedIds}
                onHover={props.onItemHover}
                onSelectionChange={setSelectedIds}
                onFocus={focusItemFromList}
                onEdit={openEditItemModal}
              />
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={otherItemsExpanded}
            onChange={(_, expanded) => setOtherItemsExpanded(expanded)}
            disableGutters
            elevation={0}
            slotProps={{ transition: { unmountOnExit: true } }}
            sx={{
              '&::before': {
                display: 'none',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                flexDirection: 'row-reverse',
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center',
                  justifyContent: 'space-between',
                },
                '& .MuiAccordionSummary-expandIconWrapper': {
                  marginRight: 1,
                },
              }}
            >
              <Box component="span" sx={{ fontWeight: 600 }}>
                {translate('resources.security-inventory-items.text.other_inventory_items', {
                  count: remainingItems.length,
                })}
              </Box>
              <IconButton
                size="small"
                onClick={(event) => {

                  event.stopPropagation();
                  setOtherItemsVisible((visible) => !visible);

                }}
              >
                {otherItemsVisible ? <VisibilityIcon color="action" /> : <VisibilityOffIcon color="disabled" />}
              </IconButton>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              <InventoryItemsDatagrid
                items={remainingItems}
                isLoading={props.itemsLoading || props.categoriesLoading || usersLoading || identityLoading}
                categories={props.categoriesData || []}
                visible={otherItemsVisible}
                selectable={otherItemsExpanded && otherItemsVisible}
                hoveredItemId={props.hoveredItemId}
                selectedIds={selectedIds}
                onHover={props.onItemHover}
                onSelectionChange={setSelectedIds}
                onFocus={focusItemFromList}
                onEdit={openEditItemModal}
              />
            </AccordionDetails>
          </Accordion>
        </div>
      </div>
      <Confirm
        isOpen={deleteDialogOpen}
        onConfirm={handleDeleteItems}
        onClose={() => setDeleteDialogOpen(false)}
        title={translate('resources.security-inventory-items.text.delete_dialog_title')}
        content={translate('resources.security-inventory-items.text.delete_content')}
      />
      <EditItemModal
        record={editItemRecord}
        categories={props.categoriesData || []}
        users={usersData || []}
        open={editItemModalOpen}
        onClose={closeEditItemModal}
      />
      <RenewalChoiceDialog
        variant="security-inventory"
        open={renewDialogOpen}
        onClose={closeRenewDialog}
        onRenewNow={() => {

          setRenewDialogChoice('renew_now');
          setRenewDialogOpen(false);

        }}
        onPlanRenewal={() => {

          setRenewDialogChoice('plan_renewal');
          setRenewDialogOpen(false);

        }}
      />
      <RenewNowModal
        formSessionKey={renewFlowSessionKey}
        open={renewDialogChoice === 'renew_now'}
        onClose={closeRenewDialog}
        onBack={() => {

          setRenewDialogChoice('closed');
          setRenewDialogOpen(true);

        }}
        onSubmit={handleRenewNowSubmit}
        timeGranularity={primaryCategory?.expiryInterval}
        title="resources.security-inventory-items.text.renew_now_modal_title"
        startDateLabel="resources.security-inventory-items.fields.last_inspection_date"
        expiryDateLabel="resources.security-inventory-items.fields.expiry_date"
        helperText="resources.security-inventory-items.text.renew_now_modal_helper_text"
      />
      <PlanRenewalModal
        formSessionKey={renewFlowSessionKey}
        open={renewDialogChoice === 'plan_renewal'}
        onClose={closeRenewDialog}
        onBack={() => {

          setRenewDialogChoice('closed');
          setRenewDialogOpen(true);

        }}
        onSubmit={handlePlanRenewalSubmit}
        dateFieldLabel="resources.security-inventory-items.fields.planned_inspection_date"
        dateFieldHelperText="resources.security-inventory-items.text.planned_inspection_date_helper_text"
        title="resources.security-inventory-items.text.plan_inspection"
      />
    </>
  );

};
